// src/pages/tenant/TenantInvoicesPage.tsx
import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Stack, TextField, InputAdornment,
  Button, Divider, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Skeleton, Alert, Tooltip,
  IconButton, useMediaQuery, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, Menu, MenuItem, ListItemIcon,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api from '../../services/api';

import SearchIcon from '@mui/icons-material/Search';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';

// ─── Types ────────────────────────────────────────────────────────────────────
type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'void';

type LineItem = { description: string; quantity: number; unitPrice: number; amount: number };

type InvoiceRow = {
  _id: string;
  id: string;
  invoiceNo: string;
  invoiceNumber: string;
  companyName: string;
  companyId: string | { _id: string; name: string; email: string };
  amount: number;
  total: number;
  currency: string;
  status: InvoiceStatus;
  periodStart: string;
  periodEnd: string;
  issuedAt: string;
  dueAt: string;
  paidAt?: string;
  paymentReference?: string;
  paymentSubmittedReference?: string;
  paymentSubmittedAt?: string;
  lineItems?: LineItem[];
  billingPlanSnapshot?: { baseFee: number; perDeviceRate: number; activeDevices: number };
  pdfUrl?: string;
};

const fmt = (n: number, currency = 'NGN') => {
  try { return new Intl.NumberFormat('en-NG', { style: 'currency', currency }).format(n); }
  catch { return `${currency} ${Number(n).toFixed(2)}`; }
};

const fmtDate = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
};

const STATUS_CONFIG: Record<InvoiceStatus, { label: string; color: string; bg: string }> = {
  draft: { label: 'Draft', color: '#94a3b8', bg: 'rgba(148,163,184,0.10)' },
  sent: { label: 'Sent', color: '#38bdf8', bg: 'rgba(56,189,248,0.12)' },
  paid: { label: 'Paid', color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  overdue: { label: 'Overdue', color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
  void: { label: 'Void', color: '#64748b', bg: 'rgba(100,116,139,0.10)' },
};

const STATUS_FILTERS = ['all', 'draft', 'sent', 'paid', 'overdue', 'void'] as const;

// ─── Mark Paid Dialog ─────────────────────────────────────────────────────────
interface MarkPaidDialogProps {
  open: boolean;
  invoice: InvoiceRow | null;
  onClose: () => void;
  onPaid: () => void;
}

const MarkPaidDialog: React.FC<MarkPaidDialogProps> = ({ open, invoice, onClose, onPaid }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [ref, setRef] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => { if (open) { setRef(''); setNotes(''); setError(''); } }, [open]);

  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const subtext = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2, fontSize: '0.875rem',
      background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
      '& fieldset': { borderColor: border },
      '&:hover fieldset': { borderColor: '#34d399' },
      '&.Mui-focused fieldset': { borderColor: '#34d399' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#34d399' },
  };

  const handleConfirm = async () => {
    if (!ref.trim()) { setError('Payment reference is required'); return; }
    setLoading(true);
    try {
      await api.patch(`/tenant-admin/invoices/${invoice!._id}/mark-paid`, {
        paymentReference: ref.trim(),
        paymentNotes: notes.trim() || undefined,
      });
      onPaid();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to mark as paid');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, background: isDark ? '#1e1e1e' : '#fff', border: `1px solid ${border}` } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 30, height: 30, borderRadius: 1.5, background: 'rgba(52,211,153,0.12)', display: 'grid', placeItems: 'center' }}>
            <CheckCircleIcon sx={{ color: '#34d399', fontSize: 16 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: '0.9rem', color: isDark ? '#fff' : '#0f172a' }}>Mark as Paid</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: subtext }}>{invoice?.invoiceNumber || invoice?.invoiceNo}</Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 1.5 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
        <Stack spacing={2}>
          <Box sx={{ p: 1.5, borderRadius: 2, background: isDark ? 'rgba(52,211,153,0.08)' : 'rgba(52,211,153,0.05)', border: '1px solid rgba(52,211,153,0.2)' }}>
            <Typography sx={{ fontSize: '0.8rem', color: subtext }}>
              Amount: <strong style={{ color: '#34d399' }}>{fmt(invoice?.total || invoice?.amount || 0, invoice?.currency)}</strong>
            </Typography>
          </Box>
          <TextField fullWidth label="Bank Transfer Reference *" value={ref} onChange={e => setRef(e.target.value)} size="small" sx={fieldSx} placeholder="e.g. TRF-20240201-001" />
          <TextField fullWidth label="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} size="small" sx={fieldSx} multiline rows={2} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, color: subtext }}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800, background: '#34d399', color: '#000', '&:hover': { background: '#2ecc71' } }}>
          {loading ? <CircularProgress size={16} /> : 'Confirm Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Invoice Detail Dialog ────────────────────────────────────────────────────
interface InvoiceDetailProps {
  open: boolean;
  invoice: InvoiceRow | null;
  onClose: () => void;
}

const InvoiceDetailDialog: React.FC<InvoiceDetailProps> = ({ open, invoice, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const subtext = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const headingColor = isDark ? '#fff' : '#0f172a';
  if (!invoice) return null;
  const cfg = STATUS_CONFIG[invoice.status];
  const total = invoice.total || invoice.amount || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, background: isDark ? '#1e1e1e' : '#fff', border: `1px solid ${border}` } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 30, height: 30, borderRadius: 1.5, background: 'rgba(251,191,36,0.12)', display: 'grid', placeItems: 'center' }}>
              <ReceiptLongIcon sx={{ color: '#fbbf24', fontSize: 16 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: '0.9rem', color: headingColor }}>{invoice.invoiceNumber || invoice.invoiceNo}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: subtext }}>{typeof invoice.companyId === 'object' ? (invoice.companyId as any).name : invoice.companyName}</Typography>
            </Box>
          </Stack>
          <Box sx={{ px: 1.5, py: 0.35, borderRadius: 2, background: cfg.bg }}>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{cfg.label}</Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2}>
          <Box>
            {[
              { label: 'Period', value: `${fmtDate(invoice.periodStart)} → ${fmtDate(invoice.periodEnd)}` },
              { label: 'Issued', value: fmtDate(invoice.issuedAt) },
              { label: 'Due', value: fmtDate(invoice.dueAt) },
              ...(invoice.paidAt ? [{ label: 'Paid', value: fmtDate(invoice.paidAt) }] : []),
              ...(invoice.paymentReference ? [{ label: 'Ref', value: invoice.paymentReference }] : []),
            ].map(item => (
              <Stack key={item.label} direction="row" justifyContent="space-between" sx={{ py: 0.75, borderBottom: `1px solid ${border}` }}>
                <Typography sx={{ fontSize: '0.78rem', color: subtext, fontWeight: 700 }}>{item.label}</Typography>
                <Typography sx={{ fontSize: '0.78rem', color: headingColor, fontWeight: 700 }}>{item.value}</Typography>
              </Stack>
            ))}
          </Box>

          {invoice.lineItems && invoice.lineItems.length > 0 && (
            <Box>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: subtext, textTransform: 'uppercase', letterSpacing: '0.07em', mb: 1 }}>Line Items</Typography>
              <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${border}`, overflow: 'hidden' }}>
                {invoice.lineItems.map((li, i) => (
                  <Stack key={i} direction="row" justifyContent="space-between" alignItems="center"
                    sx={{ px: 2, py: 1.25, borderBottom: i < invoice.lineItems!.length - 1 ? `1px solid ${border}` : 'none' }}>
                    <Box>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: headingColor }}>{li.description}</Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: subtext }}>Qty: {li.quantity} × {fmt(li.unitPrice, invoice.currency)}</Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 900, fontSize: '0.85rem', color: headingColor }}>{fmt(li.amount, invoice.currency)}</Typography>
                  </Stack>
                ))}
                <Stack direction="row" justifyContent="space-between" sx={{ px: 2, py: 1.5, background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>
                  <Typography sx={{ fontWeight: 900, fontSize: '0.85rem', color: headingColor }}>Total</Typography>
                  <Typography sx={{ fontWeight: 900, fontSize: '1rem', color: '#34d399' }}>{fmt(total, invoice.currency)}</Typography>
                </Stack>
              </Paper>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Row Actions Menu ─────────────────────────────────────────────────────────
interface RowActionsProps {
  invoice: InvoiceRow;
  onView: () => void;
  onMarkPaid: () => void;
  onVoid: () => void;
  onConfirmPayment: () => void;
}

const RowActions: React.FC<RowActionsProps> = ({ invoice, onView, onMarkPaid, onVoid, onConfirmPayment }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const subtext = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const canPay = ['sent', 'overdue'].includes(invoice.status) && !invoice.paymentSubmittedReference;
  const hasSubmittedRef = !!invoice.paymentSubmittedReference && invoice.status !== 'paid';
  const canConfirmPayment = hasSubmittedRef;

  const canVoid = ['draft', 'sent', 'overdue'].includes(invoice.status);



  return (
    <>
      <IconButton size="small" onClick={e => setAnchor(e.currentTarget)} sx={{ color: subtext }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        PaperProps={{ sx: { borderRadius: 2, background: isDark ? '#1e1e1e' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`, minWidth: 170, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
        <MenuItem onClick={() => { setAnchor(null); onView(); }}>
          <ListItemIcon><VisibilityIcon fontSize="small" sx={{ color: '#38bdf8' }} /></ListItemIcon>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>View Details</Typography>
        </MenuItem>
        {canPay && (
          <MenuItem onClick={() => { setAnchor(null); onMarkPaid(); }}>
            <ListItemIcon><CheckCircleIcon fontSize="small" sx={{ color: '#34d399' }} /></ListItemIcon>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#34d399' }}>Mark as Paid</Typography>
          </MenuItem>
        )}
        {canConfirmPayment && (
          <MenuItem onClick={() => { setAnchor(null); onConfirmPayment(); }}>
            <ListItemIcon><CheckCircleIcon fontSize="small" sx={{ color: '#34d399' }} /></ListItemIcon>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#34d399' }}>Confirm Payment</Typography>
          </MenuItem>
        )}
        {canVoid && (
          <MenuItem onClick={() => { setAnchor(null); onVoid(); }}>
            <ListItemIcon><BlockIcon fontSize="small" sx={{ color: '#f87171' }} /></ListItemIcon>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#f87171' }}>Void Invoice</Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const TenantInvoicesPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'all' | InvoiceStatus>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState<InvoiceRow[]>([]);
  const [total, setTotal] = useState(0);

  // Dialogs
  const [markPaidInvoice, setMarkPaidInvoice] = useState<InvoiceRow | null>(null);
  const [detailInvoice, setDetailInvoice] = useState<InvoiceRow | null>(null);
  const [voidLoading, setVoidLoading] = useState<string | null>(null);

  const surface = isDark ? 'rgba(255,255,255,0.03)' : '#ffffff';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const subtext = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const headingColor = isDark ? '#ffffff' : '#0f172a';

  const fetchInvoices = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/tenant-admin/invoices', {
        params: { search: search || '', status: status === 'all' ? '' : status, page: page + 1, limit: rowsPerPage },
      });
      const body = res.data;
      const list = Array.isArray(body?.data) ? body.data : [];
      setRows(list);
      setTotal(body?.pagination?.total || list.length);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load invoices');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search, status, page, rowsPerPage]);

  React.useEffect(() => { fetchInvoices(); }, [fetchInvoices]);
  React.useEffect(() => { setPage(0); }, [search, status]);

  const handleVoid = async (invoice: InvoiceRow) => {
    if (!window.confirm(`Void invoice ${invoice.invoiceNumber || invoice.invoiceNo}?`)) return;
    setVoidLoading(invoice._id);
    try {
      await api.patch(`/tenant-admin/invoices/${invoice._id}/void`);
      fetchInvoices();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to void invoice');
    } finally {
      setVoidLoading(null);
    }
  };

  const handleConfirmPayment = async (invoice: InvoiceRow) => {
    try {
      await api.patch(`/tenant-admin/invoices/${invoice._id}/confirm-payment`);
      fetchInvoices();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to confirm payment');
    }
  };

  const kpis = useMemo(() => ({
    paid: rows.filter(r => r.status === 'paid').length,
    sent: rows.filter(r => r.status === 'sent').length,
    overdue: rows.filter(r => r.status === 'overdue').length,
    sum: rows.reduce((a, r) => a + (r.total || r.amount || 0), 0),
    currency: rows[0]?.currency || 'NGN',
  }), [rows]);

  return (
    <Box>
      <MarkPaidDialog open={!!markPaidInvoice} invoice={markPaidInvoice} onClose={() => setMarkPaidInvoice(null)} onPaid={fetchInvoices} />
      <InvoiceDetailDialog open={!!detailInvoice} invoice={detailInvoice} onClose={() => setDetailInvoice(null)} />

      {/* ── Header ── */}
      <Stack direction={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'flex-start' : 'center'} justifyContent="space-between" spacing={1.5} sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 36, height: 36, borderRadius: 2, background: 'rgba(251,191,36,0.12)', display: 'grid', placeItems: 'center' }}>
            <ReceiptLongIcon sx={{ color: '#fbbf24', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.2, color: headingColor }}>Invoices</Typography>
            <Typography variant="body2" sx={{ color: subtext, fontWeight: 500 }}>Billing records across all companies</Typography>
          </Box>
        </Stack>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchInvoices} size="small" sx={{ border: `1px solid ${border}`, borderRadius: 1.5, color: subtext }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* ── KPI strip ── */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${border}`, background: surface, mb: 2 }}>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={1.5} alignItems={isMobile ? 'flex-start' : 'center'} justifyContent="space-between" flexWrap="wrap">
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {[
              { label: `Paid: ${kpis.paid}`, color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
              { label: `Sent: ${kpis.sent}`, color: '#38bdf8', bg: 'rgba(56,189,248,0.12)' },
              { label: `Overdue: ${kpis.overdue}`, color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
            ].map(b => (
              <Box key={b.label} sx={{ px: 1.5, py: 0.4, borderRadius: 2, background: b.bg }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: b.color, letterSpacing: '0.04em' }}>{b.label}</Typography>
              </Box>
            ))}
          </Stack>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: headingColor }}>
            This page: <span style={{ color: '#fbbf24' }}>{fmt(kpis.sum, kpis.currency)}</span>
          </Typography>
        </Stack>
      </Paper>

      {/* ── Filters ── */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${border}`, background: surface, mb: 2 }}>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={1.5} alignItems={isMobile ? 'stretch' : 'center'} justifyContent="space-between">
          <TextField
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search invoice no or company…" size="small"
            sx={{ minWidth: isMobile ? '100%' : 340, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.875rem', background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', '& fieldset': { borderColor: border }, '&:hover fieldset': { borderColor: '#fbbf24' }, '&.Mui-focused fieldset': { borderColor: '#fbbf24' } } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: subtext }} /></InputAdornment> }}
          />
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {STATUS_FILTERS.map(s => (
              <button key={s} onClick={() => { setPage(0); setStatus(s as any); }} style={{ padding: '4px 10px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', background: status === s ? '#fbbf24' : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', color: status === s ? '#000' : subtext, transition: 'all 0.15s' }}>
                {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </Stack>
        </Stack>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mt: 1.5, borderRadius: 2 }}>{error}</Alert>}
      </Paper>

      {/* ── Table ── */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${border}`, background: surface, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>
                {['Invoice', 'Company', 'Period', 'Issued', 'Due', 'Status', 'Amount', ''].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 800, fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: subtext, borderBottom: `1px solid ${border}`, py: 1.5 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>{Array.from({ length: 8 }).map((_, j) => <TableCell key={j} sx={{ borderBottom: `1px solid ${border}` }}><Skeleton height={20} /></TableCell>)}</TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ borderBottom: 'none' }}>
                    <Box sx={{ py: 8, textAlign: 'center' }}>
                      <ReceiptLongIcon sx={{ fontSize: 36, color: subtext, mb: 1 }} />
                      <Typography sx={{ fontWeight: 800, color: headingColor }}>No invoices found</Typography>
                      <Typography variant="body2" sx={{ color: subtext, mt: 0.5 }}>Invoices are generated automatically on billing day or manually from the Billing page</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
                
              ) : rows.map(r => {
                const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.draft;
                const amount = r.total || r.amount || 0;
                const name = typeof r.companyId === 'object' ? (r.companyId as any).name : r.companyName;
                return (
                  <TableRow key={r._id} hover sx={{ '&:hover': { background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }, '& td': { borderBottom: `1px solid ${border}` }, opacity: voidLoading === r._id ? 0.5 : 1 }}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.83rem', color: headingColor }}>{r.invoiceNumber || r.invoiceNo}</Typography>
                      <Typography variant="caption" sx={{ color: subtext, fontFamily: 'monospace', fontSize: '0.65rem' }}>{r._id}</Typography>
                    </TableCell>
                    <TableCell><Typography sx={{ fontWeight: 700, fontSize: '0.83rem', color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)' }}>{name || '—'}</Typography></TableCell>
                    <TableCell><Typography sx={{ fontWeight: 600, fontSize: '0.75rem', color: subtext }}>{fmtDate(r.periodStart)} → {fmtDate(r.periodEnd)}</Typography></TableCell>
                    <TableCell><Typography sx={{ fontSize: '0.78rem', color: subtext }}>{fmtDate(r.issuedAt)}</Typography></TableCell>
                    <TableCell><Typography sx={{ fontSize: '0.78rem', color: subtext }}>{fmtDate(r.dueAt)}</Typography></TableCell>
                    <TableCell>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.35, borderRadius: 2, background: cfg.bg }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: cfg.color }} />
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: cfg.color, letterSpacing: '0.07em', textTransform: 'uppercase' }}>{cfg.label}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right"><Typography sx={{ fontWeight: 900, fontSize: '0.875rem', color: headingColor }}>{fmt(amount, r.currency)}</Typography></TableCell>
                    <TableCell align="right">
                      <RowActions
                        invoice={r}
                        onView={() => setDetailInvoice(r)}
                        onMarkPaid={() => setMarkPaidInvoice(r)}
                        onVoid={() => handleVoid(r)}
                        onConfirmPayment={() => handleConfirmPayment(r)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider sx={{ borderColor: border }} />
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
          sx={{ color: subtext, '& .MuiTablePagination-select': { color: headingColor }, '& .MuiIconButton-root': { color: subtext } }}
        />
      </Paper>
    </Box>
  );
};

export default TenantInvoicesPage;